/* eslint-disable @typescript-eslint/no-explicit-any */
import * as History from 'history';
import * as React from 'react';
import * as Sentry from '@sentry/react';
import { ApolloProvider } from '@apollo/client';
import { AppSettings } from 'settings/AppSettings';
import { Duration } from 'js-joda';
import { HelmetProvider } from 'react-helmet-async';
import { Provider } from 'react-redux';
import { Router } from 'react-router';
import { SharedAppConstants } from '@tock/shared-app-constants';
import { StoreType } from '@tock/shared/utils/connect';
import { applyMiddleware, compose, createStore } from 'redux';
import {
  checkIgnore,
  ignoredMessages,
  initDatadogLogging,
  initGlobalLogging,
  logger,
} from '@tock/logging';
import {
  clearAuthCookie,
  getAuthCookie,
  getAuthCookieName,
  setAuthCookie,
} from '@tock/shared/utils/authCookieUtils';
import { createComplexLocalStorageEnhancer } from '@tock/shared/enhancers/LocalStorage';
import { createTockApiMiddleware } from '@tock/shared/middleware/TockApiMiddleware';
import { getAuthState } from 'utils/auth/getAuthState';
import { getCsrfCookie } from '@tock/shared/utils/csrfTokenUtils';
import { guardTimezoneChange } from '@tock/shared/utils/timezone';
import { hydrate, render } from 'react-dom';
import { includes, throttle, toLower } from 'es-toolkit/compat';
import { initMessageListener } from 'redux-state-sync';
import { initSentry } from 'sentryInit';
import { initializeApolloClient } from './utils/apollo/initializeApolloClient';
import { loadableReady } from '@loadable/component';
import {
  localPrioritizedBackend,
  sessionPrioritizedBackend,
} from 'utils/storage-backends/backends';
import { parse } from 'cookie';
import { parseUrl } from 'utils/domainUtils';
import { scrollToDepth } from '@tock/shared/utils/scroll';
import { setLegacyImageUrlLoggingEnabled } from '@tock/images';
import { setSettings } from 'actions/AppActions';
import AppConstants from 'constants/AppConstants';
import Rollbar from 'rollbar';
import TockHistory from 'utils/TockHistory';
import localePolyfill from '@tock/shared/utils/locales';
import promiseMiddleware from 'redux-promise';
import secureRandom from 'secure-random';
import thunkMiddleware from 'redux-thunk';

const tockHistory = new TockHistory();
let needsPolyfill = false;
if (!window.requestAnimationFrame) {
  window.requestAnimationFrame = ((callback: any) => setTimeout(callback, 17)) as any;
}

if (!window.location.replace) {
  try {
    window.location.replace = (path: any) => {
      // This is a polyfill for older browsers
      // eslint-disable-next-line xss/no-location-href-assign
      window.location.href = path;
    };
  } catch (_error) {
    logger.warn('Failed to polyfill replace');
  }
}

export interface ThrottleActionType {
  type: string;
  value: string;
}

const actionsToThrottle: ThrottleActionType[] = [
  {
    type: 'postProto',
    value: '/purchase',
  },
];

export const throttleActions =
  (actionsToThrottle: ThrottleActionType[], interval = 1000) =>
  () =>
  (dispatch: (args: any) => any) => {
    const throttledDispatchFunction = throttle(dispatch, interval);
    return (action: ApiAction) => {
      const foundAction = actionsToThrottle.find((actionToThrottle) => {
        return (
          (action as { TockApi: any }).TockApi &&
          actionToThrottle.value === (action as { TockApi: any }).TockApi[actionToThrottle.type]
        );
      });
      let dispatchFunction = dispatch;
      if (foundAction) {
        dispatchFunction = throttledDispatchFunction;
        if (
          process.env.NODE_ENV !== 'production' ||
          (window && includes(window.location.hostname, 'staging'))
        ) {
          // eslint-disable-next-line no-console
          console.info(`THR: Throttling action ${foundAction.type}, ${foundAction.value}`);
        }
      }

      return dispatchFunction(action);
    };
  };

function setupRollbar(appSettings: AppSettings) {
  if (process.env.NODE_ENV !== 'production') {
    return;
  }

  // Sample at a rate of 1 / 1000.
  const shouldLogCSPViolations = Math.random() < 0.001;

  const rollbarConfig = {
    accessToken: appSettings.ROLLBAR_ACCESS_TOKEN,
    captureUncaught: true,
    scrubTelemetryInputs: true,
    checkIgnore: process.env.NODE_ENV !== 'production' ? () => true : checkIgnore, // Ignore all in development
    ignoredMessages,
    autoInstrument: {
      contentSecurityPolicy: shouldLogCSPViolations, // enables telemetry tracking, enabled by default
      errorOnContentSecurityPolicy: shouldLogCSPViolations, // send CSP errors to Rollbar
    },
    payload: {
      environment: appSettings.ROLLBAR_ENV,
      client: {
        javascript: {
          source_map_enabled: true,
          code_version: (window as any).__BUILD_NUMBER__ || 'dev',
          // Optionally have Rollbar guess which frames the error was thrown from
          // when the browser does not provide line and column numbers.
          guess_uncaught_frames: true,
        },
      },
    },
  };
  window.Rollbar = new Rollbar(rollbarConfig);
}

function setupDatadog(appSettings: AppSettings) {
  if (process.env.NODE_ENV !== 'production') {
    return;
  }

  initDatadogLogging({
    clientToken: appSettings.DATADOG_CLIENT_TOKEN,
    applicationId: appSettings.DATADOG_CONSUMER_APP_ID,
    service: 'tock-consumer',
    env: appSettings.ENV,
    version: ((window as any).__BUILD_NUMBER__ || 'dev').toLowerCase(),
    loggingSampleRate: 100,
    rumSampleRate: 100,
  });

  // Setup global logging functions
  initGlobalLogging();
}

function arrayBufferToBase64(bytes: Uint8Array) {
  let binary = '';
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

export const sessionKey = 'tock_session';

function getOrCreateSession() {
  const cookie = parse(document.cookie);
  const { query } = parseUrl(location.href);

  try {
    const querySessionToken = query?.['sessionToken'];
    if (querySessionToken) {
      sessionPrioritizedBackend.set(sessionKey, querySessionToken);
      return querySessionToken;
    }

    let sessionToken = sessionPrioritizedBackend.get(sessionKey);
    if (!sessionToken) {
      const bytes = secureRandom(30, { type: 'Uint8Array' });
      sessionToken = 'client_' + arrayBufferToBase64(bytes);
      sessionPrioritizedBackend.set(sessionKey, sessionToken);
    }
    return sessionToken || cookie.JSESSIONID;
  } catch (e: any) {
    logger.error(e);
    return cookie.JSESSIONID;
  }
}

function initializeApp(
  appSettings: AppSettings,
  rootReducer: any,
  {
    sessionEnhancedStores,
    localEnhancedStores,
  }: {
    sessionEnhancedStores: string[];
    localEnhancedStores: string[];
  },
  onTransitionPage: any
) {
  try {
    (window as any).React = React;
    let state: StoreType = {} as any;
    if ((window as any).$REDUX_STATE) {
      state = (window as any).$REDUX_STATE;
    }

    const isIE = !!(window as any).MSInputMethodContext;
    const locale = toLower(state.app.locale || '');
    // Only for australia we have seen Intl bugs.
    const forcePolyfill = !locale || locale === 'en-au' || locale === 'en-gb' || isIE;
    needsPolyfill = !(window as any).Intl || forcePolyfill;
    localePolyfill(needsPolyfill);

    initSentry(appSettings, tockHistory);

    setupRollbar(appSettings);
    setupDatadog(appSettings);

    logger.init(appSettings.LOG_LEVEL);

    let store: any;
    const logoutFunction = () => {
      tockHistory.push(`${store.getState()?.app?.appContext?.pathPrefix || ''}/logout`);
    };

    const composeEnhancers = (window as any).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

    const isStaging = appSettings.DATADOG_ENV === 'staging';
    const sentryReduxEnhancer = isStaging ? Sentry.createReduxEnhancer({}) : null;

    const localStorageEnhancer = createComplexLocalStorageEnhancer([
      {
        storeBackend: sessionPrioritizedBackend,
        storeKeys: sessionEnhancedStores,
      },
      {
        storeBackend: localPrioritizedBackend,
        storeKeys: localEnhancedStores,
      },
    ]);

    const tockMiddleware = createTockApiMiddleware({
      apiBase: appSettings.API_BASE,
      appConstants: {
        ...AppConstants,
        ...SharedAppConstants,
      },
      logoutAction: logoutFunction,
      getAuthState,
      appSettings,
    });

    const middleware = [tockMiddleware, thunkMiddleware, promiseMiddleware];

    const enhancers = isStaging
      ? [applyMiddleware(...middleware), sentryReduxEnhancer, localStorageEnhancer]
      : [applyMiddleware(...middleware), localStorageEnhancer];

    const enhancer = composeEnhancers(...enhancers);

    store = createStore(rootReducer, state, enhancer);
    if (
      process.env.NODE_ENV !== 'production' ||
      (window && includes(window.location.hostname, 'staging'))
    ) {
      // eslint-disable-next-line no-console
      console.info('THR: Recreating store');
    }
    const throttleMiddleware = throttleActions(actionsToThrottle);
    const middlewareWithThrottle = [throttleMiddleware, ...middleware];
    const [, ...tail] = enhancers;
    const enhancersWithThrottle = composeEnhancers(
      ...[applyMiddleware(...middlewareWithThrottle), ...tail]
    );
    store = createStore(rootReducer, state, enhancersWithThrottle);

    setLegacyImageUrlLoggingEnabled(
      !!store.getState().features.features.ENABLE_IMGIX_IMAGE_URLS_DATADOG_LOGGING
    );

    tockHistory.store = store;
    store.dispatch(setSettings(appSettings));
    initMessageListener(store);

    guardTimezoneChange();

    if (window) {
      (window as any).TOUCH_ENABLED = 'ontouchstart' in window;

      if (!(window as any).TOUCH_ENABLED && document.body && document.body.classList) {
        document.body.classList.add('no-touch');
      }
      if (store.getState().app.inReactNative && document.body && document.body.classList) {
        document.body.classList.add('Body-native');
      }
      if ((window as any).supportsWebp) {
        document.body.classList.add('webp');
      } else {
        document.body.classList.add('no-webp');
      }

      let supportsFocusVisibleClassName = 'focus-visible';
      try {
        document.querySelector(':focus-visible');
      } catch (_error) {
        supportsFocusVisibleClassName = 'no-focus-visible';
      }
      document.body.classList.add(supportsFocusVisibleClassName);

      setUpMobileNativeListeners();
      setSameSiteAttributeOnCookiesIfNecessary();

      const { hostname } = window.location;
      const jwtToken = getAuthCookie(hostname);
      const csrfToken = getCsrfCookie(hostname);

      const sessionToken = getOrCreateSession();
      store.dispatch({
        type: AppConstants.RESTORE_AUTH,
        payload: {
          jwtToken,
          sessionToken,
          csrfToken,
        },
      });
    }

    if (onTransitionPage) {
      // Pass in an unbound function for history.listen.
      tockHistory.listen((location: History.Location) => {
        onTransitionPage(store, location);
      });
    }

    const doc = document.documentElement;
    if (doc) {
      doc.setAttribute('data-useragent', navigator.userAgent);
    }

    return store;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
    throw e;
  }
}

function renderInternal(store: any, Application: any, fullRender?: boolean) {
  // will put back strict mode after https://github.com/mui-org/material-ui/issues/18018 is fixed, v5 is supposed to be
  const apolloClient = initializeApolloClient(store);
  try {
    if (fullRender) {
      render(
        <Provider store={store}>
          <ApolloProvider client={apolloClient}>
            <Router history={tockHistory}>
              <HelmetProvider>
                <Application />
              </HelmetProvider>
            </Router>
          </ApolloProvider>
        </Provider>,
        document.querySelector('#Root')
      );
    } else {
      loadableReady(() => {
        hydrate(
          <Provider store={store}>
            <ApolloProvider client={apolloClient}>
              <Router history={tockHistory}>
                <HelmetProvider>
                  <Application />
                </HelmetProvider>
              </Router>
            </ApolloProvider>
          </Provider>,
          document.querySelector('#Root')
        );
      });
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
    throw e;
  }
}

let counter = 0;
function renderApp(store: any, routes: any, fullRender?: boolean): void {
  if (!needsPolyfill || (window as any).IntlPolyfillLoaded || ++counter > 20) {
    renderInternal(store, routes, fullRender);
    return;
  }

  setTimeout(() => {
    renderApp(store, routes, fullRender);
  }, 100);
}

export default {
  initializeApp,
  renderApp,
};

function setUpMobileNativeListeners() {
  document.addEventListener('deviceready', () => {
    window.addEventListener('keyboardDidShow', () => {
      (document.activeElement as any).scrollIntoView(false);
    });
    window.addEventListener('statusTap', function () {
      scrollToDepth(document.body, 0, {});
      scrollToDepth(document.documentElement, 0, {});
    });
  });
}

/**
 * Hack function to update auth cookies to have the SameSite=None attribute.
 * We have no access to cookie attributes from Javascript, so we have no way
 * of knowing whether an existing auth cookie already has the proper attributes.
 * As a result, the only way to be sure the cookie has SameSite: None is to set
 * it again every time the user starts up the app.
 *
 * TODO(jacob): Some time after around October 1, 2020, remove this function
 * since almost everyone will have a properly-attributed cookie.
 */
function setSameSiteAttributeOnCookiesIfNecessary() {
  try {
    const allCookiesString = document.cookie;
    const cookies = allCookiesString.split(/; */);
    const authCookieName = getAuthCookieName(window.location.hostname);
    let found = false;
    cookies.forEach((cookie) => {
      const [name, valueJson] = cookie.split(/=/);
      // Search for the non-legacy auth cookie name only. We don't need to refresh
      // the legacy name, since if it exists that means we're also setting non-legacy
      // with a correct SameSite=None attribute.
      if (name === authCookieName) {
        const value = JSON.parse(valueJson);
        setAuthCookie(window.location.hostname, value, Duration.ofDays(365).toMillis());
        found = true;
      }
    });
    if (!found) {
      clearAuthCookie(window.location.hostname);
    }
  } catch (e) {
    // Don't crash the app if something here fails, since that would be a worse
    // failure mode than this just not working
    logger.error('Failed to update attributes for cookies', e);
  }
}
